import React, { useContext } from 'react';
import _merge from 'lodash/merge';
import _isEmpty from 'lodash/isEmpty';
import { getContext } from 'common';
import { ChipContext } from './Chip';
import { ChipContext as InfoChipContext } from './InfoChip';

import Styled from './styles';

export interface ChipLabelProps extends React.ComponentPropsWithRef<'div'> {
  /** The label of the Chip. */
  label?: string;
  /** To configure the primary label element. */
  labelProps?: React.ComponentPropsWithRef<'span'>;
  /** To configure the secondary label element. */
  secondaryLabelProps?: React.ComponentPropsWithRef<'span'>;
  /** The secondary label of the Chip. */
  secondaryLabel?: string;
  /** If `true`. Renders labels stacked. Note: Small stacked chip is not supported. */
  stacked?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/** Any other props than the `ChipLabel` props will be provided to the root element, the size of the `ChipLabel` is read from `ChipContext`. */
const ChipLabel = React.forwardRef<HTMLElement, ChipLabelProps>((props: ChipLabelProps, ref) => getContext(({ RTL, theme }) => {
  const {
    label,
    secondaryLabel,
    stacked,
    theme: themeProp,
    labelProps,
    secondaryLabelProps,
    ...otherProps
  } = props;

  const resolvedContext: any = _isEmpty(useContext(ChipContext)) ? InfoChipContext : ChipContext;

  const {
    size,
    focused,
    hovered,
    selected,
    error,
    infoChipStatus,
    isPressed,
  } = useContext(resolvedContext);

  const resolvedTheme = _merge({}, infoChipStatus ? theme.INFO_CHIP[infoChipStatus] : theme.CHIP, themeProp);
  const hasSecondaryLabel = secondaryLabel !== undefined;
  const elementProps = {
    RTL,
    $theme: resolvedTheme,
    infoChip: !!infoChipStatus,
    size,
    focused,
    hovered,
    selected,
    stacked,
    error
  };
  return (
    <Styled.ChipLabel
      ref={ref}
      RTL={RTL}
      {...otherProps}
      size={size}
      stacked={stacked}
      $hasSecondaryLabel={hasSecondaryLabel}
      $isPressed={isPressed}
    >
      <Styled.Label {...{ ...elementProps, ...labelProps }}>
        {label}
      </Styled.Label>
      {hasSecondaryLabel &&
        <Styled.SecondaryLabel stacked={stacked} {...{ ...elementProps, ...secondaryLabelProps }}>
          {secondaryLabel}
        </Styled.SecondaryLabel>
      }
    </Styled.ChipLabel>
  );
}));

ChipLabel.displayName = 'ChipLabel';

ChipLabel.defaultProps = {
  label: undefined,
  labelProps: {},
  secondaryLabelProps: {},
  secondaryLabel: undefined,
  theme: {},
  stacked: false
};

export default ChipLabel;
